import React from "react";
import { Button, FormGroup, Input, Label } from "reactstrap";
import { useAppSettings } from "./AppSettingsContext";
import { useContext } from "react";
import UserContext from "../auth/UserContext";
import { presetLogos, resolveBrandLogoUrl } from "./logoAssets";
import "./AppSettingsPage.css";

const garageFieldOptions = [
    { key: "podium", label: "Podium" },
    { key: "ticket", label: "Ticket" },
    { key: "mobile", label: "Mobile" },
    { key: "make", label: "Make" },
    { key: "color", label: "Color" },
    { key: "parkingSpot", label: "Parking Spot" },
    { key: "note", label: "Note" },
    { key: "status", label: "Current Status" },
    { key: "payment", label: "Payment" },
];

function themeModeLabel(themeMode) {
    if (themeMode === "dark") return "Dark";
    if (themeMode === "light") return "Light";
    return "Auto";
}

const AppSettingsPage = () => {
    const { userDetail } = useContext(UserContext);
    const { settings, setThemeMode, setBranding, setGarageDetailSetting, setGarageFieldVisibility, resetAppSettings } = useAppSettings();

    const branding = settings?.branding || {};
    const garageDetail = settings?.garageDetail || {};
    const visibleFields = garageDetail?.visibleFields || {};
    const canEditBranding = Number(userDetail?.roleId || 0) >= 4;

    function handleLogoFileChange(evt) {
        if (!canEditBranding) return;
        const file = evt.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl = String(reader.result || "");
            if (dataUrl) {
                setBranding({
                    logoType: "image",
                    logoUrl: dataUrl,
                });
            }
        };
        reader.readAsDataURL(file);
    }

    return (
        <section className="page-scroll">
            <div className="page-scroll-inner app-settings-shell">
                <div className="app-settings-header">
                    <h2>Branding and App Settings</h2>
                    <p>Customize app-wide appearance, branding, and garage detail visibility without code changes.</p>
                </div>

                <div className="app-settings-grid">
                    <div className="app-settings-card">
                        <h3>Theme Mode</h3>
                        <p>Default focus is light, dark, or automatic mode based on OS setting.</p>

                        <FormGroup>
                            <Label for="themeMode">Theme</Label>
                            <Input
                                id="themeMode"
                                type="select"
                                value={settings.themeMode}
                                onChange={(evt) => setThemeMode(evt.target.value)}>
                                <option value="auto">Auto (OS)</option>
                                <option value="light">Light</option>
                                <option value="dark">Dark</option>
                            </Input>
                        </FormGroup>

                        <div className="app-settings-inline-note">Current mode: {themeModeLabel(settings.themeMode)}</div>
                    </div>

                    <div className="app-settings-card">
                        <h3>Logo and Brand</h3>
                        <p>{canEditBranding ? "Set a top-left logo image or fallback to text branding." : "Branding is managed by Director/Admin. You can view current settings."}</p>

                        <FormGroup>
                            <Label for="brandingType">Brand Display</Label>
                            <Input
                                id="brandingType"
                                type="select"
                                value={branding.logoType || "text"}
                                disabled={!canEditBranding}
                                onChange={(evt) => setBranding({ logoType: evt.target.value })}>
                                <option value="text">Text</option>
                                <option value="image">Logo Image</option>
                            </Input>
                        </FormGroup>

                        <FormGroup>
                            <Label for="brandingText">Brand Text</Label>
                            <Input
                                id="brandingText"
                                type="text"
                                maxLength={40}
                                value={branding.brandText || ""}
                                disabled={!canEditBranding}
                                onChange={(evt) => setBranding({ brandText: evt.target.value })}
                                placeholder="ParkPilot"
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label for="brandingLogoUrl">Logo URL or public path</Label>
                            <Input
                                id="brandingLogoUrl"
                                type="text"
                                value={branding.logoUrl || ""}
                                disabled={!canEditBranding}
                                onChange={(evt) => setBranding({ logoUrl: resolveBrandLogoUrl(evt.target.value) })}
                                placeholder="/images/ParkPilotLogo.PNG"
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label for="brandingLogoUpload">Upload Logo</Label>
                            <Input
                                id="brandingLogoUpload"
                                type="file"
                                accept="image/*"
                                disabled={!canEditBranding}
                                onChange={handleLogoFileChange}
                            />
                        </FormGroup>

                        <div className="app-settings-preset-grid">
                            {presetLogos.map((logo) => (
                                <Button
                                    key={logo.value}
                                    type="button"
                                    color="secondary"
                                    size="sm"
                                    disabled={!canEditBranding}
                                    onClick={() => setBranding({ logoType: "image", logoUrl: logo.value })}>
                                    {logo.label}
                                </Button>
                            ))}
                        </div>
                    </div>

                    <div className="app-settings-card app-settings-card-wide">
                        <h3>Garage Detail Visibility</h3>
                        <p>Control privacy and what fields are shown on the garage detail page.</p>

                        <div className="app-settings-row">
                            <label className="app-settings-checkbox">
                                <input
                                    type="checkbox"
                                    checked={Boolean(garageDetail.maskPhoneFirstSeven)}
                                    onChange={(evt) => setGarageDetailSetting({ maskPhoneFirstSeven: evt.target.checked })}
                                />
                                Hide first 7 digits of phone number (show only last 4)
                            </label>
                        </div>

                        <div className="app-settings-row">
                            <label className="app-settings-checkbox">
                                <input
                                    type="checkbox"
                                    checked={Boolean(garageDetail.showFieldLabels)}
                                    onChange={(evt) => setGarageDetailSetting({ showFieldLabels: evt.target.checked })}
                                />
                                Show field labels (column names)
                            </label>
                        </div>

                        <div className="app-settings-columns-grid">
                            {garageFieldOptions.map((field) => (
                                <label
                                    key={field.key}
                                    className="app-settings-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={Boolean(visibleFields[field.key])}
                                        onChange={(evt) => setGarageFieldVisibility(field.key, evt.target.checked)}
                                    />
                                    {field.label}
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="app-settings-footer">
                    <Button
                        color="danger"
                        type="button"
                        onClick={resetAppSettings}>
                        Reset App Settings
                    </Button>
                </div>
            </div>
        </section>
    );
};

export default AppSettingsPage;
